import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi, urlApi } from './api';

/**
 * Paramètres de facturation : séries de numérotation des factures et des avoirs.
 *
 * Le format du numéro, la remise à zéro annuelle et le plancher d'un réglage
 * sont des règles de l'API (`domain/facturation.ts`) : l'écran affiche ce
 * qu'elle rend, prochain numéro compris, sans rien recalculer.
 */

export type NatureFacture = 'FACTURE' | 'AVOIR';

export interface SerieFacturation {
  nature: NatureFacture;
  libelle: string;
  /** Vide : numéro sans préfixe, `2026-000597`. */
  prefixe: string;
  /** Dernier numéro attribué dans l'année en cours. */
  dernierNumero: number;
  remiseAZeroAnnuelle: boolean;
  /** Chiffres du rang, complétés par des zéros. */
  longueurRang: number;
  prochainNumero: string;
  /** Plus haut numéro déjà émis sous le réglage actuel. */
  dernierNumeroEmis: string | null;
  /** Le dernier numéro ne descend pas en dessous. */
  plancher: number;
  regleeLe: string | null;
  regleePar: string | null;
}

export interface ReglageSerie {
  prefixe: string;
  dernierNumero: number;
  remiseAZeroAnnuelle: boolean;
  longueurRang: number;
}

const CLE = ['facturation', 'series'];

export function useSeriesFacturation() {
  return useQuery({
    queryKey: CLE,
    queryFn: async () =>
      (await appelApi<{ series: SerieFacturation[] }>('/facturation/series')).series,
  });
}

export function useReglerSerieFacturation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ nature, reglage }: { nature: NatureFacture; reglage: ReglageSerie }) =>
      appelApi<{ serie: SerieFacturation }>(`/facturation/series/${nature}`, {
        methode: 'PUT',
        corps: reglage,
      }),
    // Les deux séries se lisent ensemble : le préfixe de l'une borne l'autre.
    onSuccess: () => client.invalidateQueries({ queryKey: CLE }),
  });
}

// ---------------------------------------------------------------------------
// Mentions de l'émetteur (TF-407)
// ---------------------------------------------------------------------------

/** Ce que l'écran règle, et ce que la plateforme sait déjà de l'organisme. */
export interface Emetteur {
  mentionsJuridiques: string | null;
  numeroTva: string | null;
  iban: string | null;
  bic: string | null;
  complet: boolean;
  manques: string[];
  detail: string | null;
  identite: {
    nom: string;
    siege: string;
    numeroDeclarationActivite: string;
    antennes: { code: string; nom: string; siret: string }[];
  };
  regleLe: string | null;
  reglePar: string | null;
}

export interface SaisieEmetteur {
  mentionsJuridiques: string;
  numeroTva: string;
  iban: string;
  bic: string;
}

const CLE_EMETTEUR = ['facturation', 'emetteur'];

export function useEmetteur() {
  return useQuery({
    queryKey: CLE_EMETTEUR,
    queryFn: async () => (await appelApi<{ emetteur: Emetteur }>('/facturation/emetteur')).emetteur,
  });
}

export function useReglerEmetteur() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (saisie: SaisieEmetteur) =>
      appelApi<{ emetteur: Emetteur }>('/facturation/emetteur', { methode: 'PUT', corps: saisie }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CLE_EMETTEUR });
      // L'état de l'émetteur décide de ce que le bloc Financement laisse émettre.
      void client.invalidateQueries({ queryKey: ['financements'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Certificats de réalisation (TF-412)
// ---------------------------------------------------------------------------

/** Ce que chaque certificat de réalisation reprend sans qu'on le ressaisisse. */
export interface ReglageCertificat {
  /** Civilité comprise : « M. MELADE Thierry ». */
  signataireNom: string;
  signataireQualite: string;
  lieuSignature: string;
  /** Les valeurs du modèle remis par TOP FORMATION s'appliquent encore. */
  parDefaut: boolean;
  /** Image du cachet et de la signature ; `null` : le cadre se signe à la main. */
  cachet: { typeMime: string; deposeLe: string | null; deposePar: string | null } | null;
  regleLe: string | null;
  reglePar: string | null;
}

export interface SaisieReglageCertificat {
  signataireNom: string;
  signataireQualite: string;
  lieuSignature: string;
}

const CLE_CERTIFICATS = ['facturation', 'certificats'];

/** L'image déposée, servie par une route gardée et tracée. */
export function urlCachetCertificat(): string {
  return urlApi('/facturation/certificats/cachet');
}

export function useReglageCertificat() {
  return useQuery({
    queryKey: CLE_CERTIFICATS,
    queryFn: async () =>
      (await appelApi<{ reglage: ReglageCertificat }>('/facturation/certificats')).reglage,
  });
}

function useInvalidationCertificats() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: CLE_CERTIFICATS });
    // Les propositions de certificat reprennent ce réglage.
    void client.invalidateQueries({ queryKey: ['certificats'] });
  };
}

export function useReglerCertificat() {
  const invalider = useInvalidationCertificats();
  return useMutation({
    mutationFn: (saisie: SaisieReglageCertificat) =>
      appelApi<{ reglage: ReglageCertificat }>('/facturation/certificats', { methode: 'PUT', corps: saisie }),
    onSuccess: invalider,
  });
}

export function useDeposerCachet() {
  const invalider = useInvalidationCertificats();
  return useMutation({
    mutationFn: (fichier: File) => {
      const formulaire = new FormData();
      formulaire.append('fichier', fichier);
      return appelApi<{ reglage: ReglageCertificat }>('/facturation/certificats/cachet', {
        methode: 'PUT',
        formulaire,
      });
    },
    onSuccess: invalider,
  });
}

export function useRetirerCachet() {
  const invalider = useInvalidationCertificats();
  return useMutation({
    mutationFn: () =>
      appelApi<{ reglage: ReglageCertificat }>('/facturation/certificats/cachet', { methode: 'DELETE' }),
    onSuccess: invalider,
  });
}
